import type { ICellRendererParams } from 'ag-grid-community';
import type { CompetitorSourceGapRow } from '../types';
import { BrandFavicon } from '../components/BrandFavicon';
import { SourceDisplayTypeBadge } from '../components/sources/SourceDisplayTypeBadge';
import { SourceGapStatusBadge } from '../components/sources/SourceGapStatusBadge';
import { formatSourceCount, sourceDisplayLabel } from '../utils/source-insights';
import { getHelpHtml } from '../utils/help/help-content';

function columnHelp(key: string): string | undefined {
  return getHelpHtml(key) || undefined;
}

export interface CompetitorSourceGapColumnCallbacks {
  expandedId: string | null;
  onToggle: (sourceDomainId: string) => void;
}

export function competitorSourceGapColumnConfig(callbacks: CompetitorSourceGapColumnCallbacks) {
  return [
    {
      field: 'host',
      title: 'منبع',
      tooltip: columnHelp('sections.identified_sources_table'),
      type: 'string',
      flex: 1,
      minWidth: 220,
      searchable: true,
      cellRenderer: (params: ICellRendererParams<CompetitorSourceGapRow>) => {
        const row = params.data;
        if (!row) return null;
        const label = sourceDisplayLabel(row.host, row.displayName);
        const displayName = row.displayName?.trim() ?? '';
        const showHostSubtitle =
          displayName !== '' && displayName.toLowerCase() !== row.host.toLowerCase();
        return (
          <button
            type="button"
            className="btn btn-link p-0 text-start text-decoration-none w-100 d-flex align-items-center gap-2"
            onClick={() => callbacks.onToggle(row.sourceDomainId)}
          >
            <BrandFavicon host={row.host} name={label} size="sm" />
            <span className="min-w-0">
              <span className="d-block text-truncate fw-medium text-body">{label}</span>
              {showHostSubtitle ? (
                <span className="d-block text-truncate fs--2 text-body-tertiary">{row.host}</span>
              ) : null}
            </span>
          </button>
        );
      },
    },
    {
      field: 'displayType',
      title: 'نوع',
      tooltip: columnHelp('sections.source_types'),
      type: 'string',
      width: 120,
      searchable: true,
      cellRenderer: (params: ICellRendererParams<CompetitorSourceGapRow>) =>
        params.data ? <SourceDisplayTypeBadge type={params.data.displayType} /> : null,
    },
    {
      field: 'competitorsUsing',
      title: 'رقبا',
      tooltip: columnHelp('concepts.competitor'),
      type: 'string',
      minWidth: 180,
      sortable: false,
      searchable: false,
      cellRenderer: (params: ICellRendererParams<CompetitorSourceGapRow>) => {
        const row = params.data;
        if (!row) return null;
        const names = row.competitorsUsing.map((item) => item.brandName).join('، ');
        return <span className="text-700">{names || '—'}</span>;
      },
    },
    {
      field: '_competitorCitations',
      title: 'Citation رقبا',
      tooltip: columnHelp('metrics.total_citations'),
      type: 'string',
      width: 130,
      searchable: false,
      sortable: false,
      cellRenderer: (params: ICellRendererParams<CompetitorSourceGapRow>) => {
        const row = params.data;
        if (!row) return null;
        const total = row.competitorsUsing.reduce((sum, item) => sum + item.citations, 0);
        return (
          <span style={{ fontVariantNumeric: 'tabular-nums' }}>{formatSourceCount(total)}</span>
        );
      },
    },
    {
      field: 'youCitations',
      title: 'Citation شما',
      tooltip: columnHelp('metrics.total_citations'),
      type: 'string',
      width: 130,
      searchable: true,
      cellRenderer: (params: ICellRendererParams<CompetitorSourceGapRow>) =>
        params.data ? (
          <span style={{ fontVariantNumeric: 'tabular-nums' }}>
            {formatSourceCount(params.data.youCitations)}
          </span>
        ) : null,
    },
    {
      field: 'gapType',
      title: 'وضعیت شکاف',
      tooltip: columnHelp('sections.competitor_source_gap'),
      type: 'string',
      width: 150,
      searchable: true,
      cellRenderer: (params: ICellRendererParams<CompetitorSourceGapRow>) =>
        params.data ? <SourceGapStatusBadge gapType={params.data.gapType} /> : null,
    },
  ];
}
